import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProjectApi from '../project/projectApi';
import { Project } from './projectType';
import { getCurrentTimestamp } from '../lib/global_function';
import { fetchProjectType } from '../reference/referenceApi';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import { Save } from 'lucide-react';

const UpdateProjectPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Update', path: '/main/project/update' },
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const projectAndCustomer = location.state?.projectAndCustomer as Project;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [projectTypeOptions, setProjectTypeOptions] = useState<string[]>([]);

  const updateTimeStamp = getCurrentTimestamp();
  const [project, setProject] = useState<Partial<Project>>({
    project_id: projectAndCustomer?.project_id || '',
    name: projectAndCustomer?.name || '',
    project_description: projectAndCustomer?.project_description || '',
    project_type: projectAndCustomer?.project_type || '',
    start_date: projectAndCustomer?.start_date || '',
    end_date: projectAndCustomer?.end_date || '',
    created_at: updateTimeStamp,
  });

  useEffect(() => {
    const loadProjectType = async () => {
      try {
        const projectTypeData = await fetchProjectType();
        setProjectTypeOptions(projectTypeData);
      } catch (error) {
        setErrorMessage('Failed to load project types.');
      }
    };
    loadProjectType();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProject((prevProject) => ({
      ...prevProject,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Clear previous error messages
    setErrorMessage(null);
    
    // Validate that end date is greater than or equal to start date
    if (project.start_date && project.end_date) {
     // console.log('Start date:', project.start_date);
     // console.log('End date:', project.end_date);
    //  console.log('Comparison:', project.end_date < project.start_date);
      
      if (project.end_date < project.start_date) {
        setErrorMessage('Project End Date must be greater than or equal to Project Start Date');
        return;
      }
    }
    
    try {
      await ProjectApi.update(String(project.project_id), project as Project);
      setMessage('Project updated successfully');

      // Optional: Navigate back after a delay
      //setTimeout(() => {
      //  navigate('/main/project');
     // }, 2000);
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h2 className="text-2xl font-semibold text-foreground mb-6">Update Project</h2>

          {/* Success Message */}
          {message && (
            <div>
              <Alert variant="success" className="mb-6">
                {message}
              </Alert>
              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={() => navigate('/main/project')}
                  size="medium" variant="default"
                >
                  Back to Projects
                </Button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {!message && (
            <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
              {/* Project Name */}
              <div>
                <Label htmlFor="name">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={project.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter project name"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="project_description" >
                  Description <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="project_description"
                  name="project_description"
                  value={project.project_description}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter project description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical min-h-[100px]"
                />
              </div>

              {/* Project Type */}
              <div>
                <Label htmlFor="project_type" className="block text-sm font-medium text-foreground mb-2">
                  Project Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  id="project_type"
                  name="project_type"
                  value={project.project_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="" disabled>
                    Select Project Type
                  </option>
                  {projectTypeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Start Date */}
              <div>
                <Label htmlFor="start_date">
                  Start Date
                </Label>
                <Input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={project.start_date}
                  onChange={handleInputChange}
                />
              </div>

              {/* End Date */}
              <div>
                <Label htmlFor="end_date">
                  End Date
                </Label>
                <Input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={project.end_date}
                  onChange={handleInputChange}
                />
              </div>

              {/* Submit Button */}
              <Button type="submit" size="medium" variant="default">
                <Save size={20} />
                Save Changes
              </Button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default UpdateProjectPage;